import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { MessageSquare, Send } from "lucide-react";

export const SmsStatusCard = () => {
  const [status, setStatus] = useState(null);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/sms/status").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    if (!phone) return toast.error("Enter a number to test");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sms/test", { phone });
      toast.success(`Test SMS sent to ${data.to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!status) return null;

  return (
    <Card className="border-border" data-testid="sms-status-card">
      <CardContent className="p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 mb-1">
          <h3 className="font-display text-2xl flex items-center gap-2"><MessageSquare size={18}/> SMS Verification</h3>
          <Badge
            variant="outline"
            className={status.enabled
              ? "border-primary/40 text-primary bg-primary/10"
              : "border-amber-500/50 text-amber-700 bg-amber-500/10"}
            data-testid="sms-status-badge"
          >
            {status.enabled ? "Twilio live" : "Not configured"}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Guests verify their phone with a one-time code before ordering or tracking an order.
          {status.enabled
            ? ` Codes are sent from ${status.sender}.`
            : " Until Twilio is complete, codes are only shown on screen in this preview and are never sent to real customers."}
        </p>

        {!status.enabled && (
          <ul className="text-xs text-muted-foreground space-y-1 mb-5" data-testid="sms-missing-list">
            {status.missing.map((m) => (
              <li key={m} className="flex items-start gap-2">
                <span className="text-amber-600 mt-0.5">•</span><span>Missing: <code className="text-foreground">{m}</code></span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <Label className="overline">Send a test SMS to</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98xxxxxxxx"
              className="mt-2 bg-transparent"
              data-testid="sms-test-phone"
            />
          </div>
          <Button
            onClick={sendTest}
            disabled={busy || !status.enabled}
            className="btn-pill bg-primary text-primary-foreground h-10"
            data-testid="sms-test-send"
          >
            <Send size={14} className="mr-1.5"/> {busy ? "Sending…" : "Send Test"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SmsStatusCard;
